import React from 'react';
import { Switch } from '@/components/ui/switch';
import { Mail, Bell } from 'lucide-react';

export const NOTIFICATION_CATEGORIES = [
  { key: 'task_reminders', label: 'Task reminders', desc: 'Assigned tasks and upcoming due dates' },
  { key: 'signal_reminders', label: 'Daily signal check', desc: 'Reminders to complete your shift signal check' },
  { key: 'security_alerts', label: 'Security alerts', desc: 'New-device sign-ins and account security events' },
  { key: 'weekly_digest', label: 'Weekly digest', desc: 'A weekly summary of your team\'s safety signals' },
  { key: 'announcements', label: 'Announcements', desc: 'Important updates from your organization' },
];

// Section 6 default preferences — Email + in-app channels enabled by default.
export const DEFAULT_NOTIFICATION_PREFERENCES = {
  channels: { email: true, in_app: true },
  categories: {
    task_reminders: true,
    signal_reminders: true,
    security_alerts: true,
    weekly_digest: true,
    announcements: true,
  },
};

// Section 6 — Notification Preferences. Controlled via `value` + `onChange(patch)`.
export default function NotificationPreferencesSection({ value, onChange }) {
  const v = value || DEFAULT_NOTIFICATION_PREFERENCES;
  const channels = v.channels || {};
  const categories = v.categories || {};

  const setChannel = (key, on) => onChange({ ...v, channels: { ...channels, [key]: on } });
  const setCategory = (key, on) => onChange({ ...v, categories: { ...categories, [key]: on } });

  return (
    <div className="space-y-5" data-testid="notification-preferences-section">
      <div className="space-y-3">
        <p className="text-sm font-semibold">How should we reach you?</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" />
              <div>
                <p className="text-sm font-medium">Email</p>
                <p className="text-xs text-muted-foreground">To your account email</p>
              </div>
            </div>
            <Switch
              checked={!!channels.email}
              onCheckedChange={(c) => setChannel('email', !!c)}
              data-testid="channel-email-switch"
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-primary" />
              <div>
                <p className="text-sm font-medium">In-app</p>
                <p className="text-xs text-muted-foreground">Notifications inside SAFER-i</p>
              </div>
            </div>
            <Switch
              checked={!!channels.in_app}
              onCheckedChange={(c) => setChannel('in_app', !!c)}
              data-testid="channel-inapp-switch"
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-semibold">What should we notify you about?</p>
        <div className="space-y-2">
          {NOTIFICATION_CATEGORIES.map((cat) => (
            <div key={cat.key} className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">{cat.label}</p>
                <p className="text-xs text-muted-foreground">{cat.desc}</p>
              </div>
              <Switch
                checked={categories[cat.key] !== false}
                onCheckedChange={(c) => setCategory(cat.key, !!c)}
                data-testid={`category-${cat.key}-switch`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
